<script setup lang="ts">
import Dialog from '@/components/Dialog/Dialog.vue';
import { useToggle } from '@vueuse/core';
import { QaEquipmentRtSystemDto } from '@/api/generator';
import { ref, onMounted, watch } from 'vue';
import { api } from '@/api';

const props = defineProps<{
  unicheck?: QaEquipmentRtSystemDto;
}>();

const options = ref<string[]>([]);
const selectedOption = ref<string[]>([]);
const [dialogOpen, toggleDialog] = useToggle(false);

async function updateSelectedOption(masterDeviceId = props.unicheck?.masterDeviceId ?? undefined) {
  if (masterDeviceId) {
    const { result } = await api.unicheckSimulatorApi.getSelectedCsvFileName({
      masterDeviceId,
    });

    selectedOption.value = result?.name ? [result.name] : [];
  }
}

async function selectOption(
  identifier: string[],
  masterDeviceId = props.unicheck?.masterDeviceId ?? undefined,
) {
  if (masterDeviceId && identifier.length) {
    const { success } = await api.unicheckSimulatorApi.selectCsvFile({
      identifier: identifier[0],
      masterDeviceId,
    });

    if (success) {
      selectedOption.value = identifier;
      toggleDialog(false);
    }
  }
}

onMounted(async () => {
  await updateSelectedOption();
});

watch(
  () => props.unicheck,
  async () => {
    await updateSelectedOption();
  },
);

// Fetch options when dialog opens (lazy loading)
watch(dialogOpen, async (isOpen) => {
  if (isOpen && options.value.length === 0) {
    const { result } = await api.unicheckSimulatorApi.getCsvFiles();
    options.value = result ? result.toSorted() : [];
  }
});

function toggle(open: boolean = true) {
  if (props.unicheck) {
    toggleDialog(open);
  }
}
</script>

<template>
  <div>
    <slot :toggle="toggle">
      <v-btn size="x-small" variant="text" text="measurement" @click="toggle()" />
    </slot>

    <Dialog v-model="dialogOpen">
      <template #title> {{ $t('ChooseInputSensorDataForMeasurement') }} </template>

      <template #text>
        <v-list
          :items="options"
          selectable
          selectStrategy="single-leaf"
          returnObject
          :selected="selectedOption"
          @update:selected="selectOption"
        />
      </template>
    </Dialog>
  </div>
</template>
